import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Container from '../components/Container';
import SectionTitle from '../components/SectionTitle';
import Button from '../components/Button';
import Icon from '../components/Icon';
import VideoThumbnail from '../components/VideoThumbnail';
import ImageCarousel from '../components/ImageCarousel';
import OptimizedImage from '../components/OptimizedImage';

// Import content
import servicesContent from '../content/services.json';

/**
 * Services page component with optimized images
 * @returns {React.ReactElement} Services page
 */
const ServicesPage = () => {
  // Get services from JSON file
  const { services } = servicesContent;

  // All services are now active
  const activeServices = services; // Photography, Videography, and Aerial Mapping

  return (
    <div className="min-h-screen flex flex-col bg-brand-black text-white">
      <Header />
      
      {/* Page Header */}
      <section className="pt-40 md:pt-48 lg:pt-56 pb-12 bg-brand-black text-white">
        <Container>
          <SectionTitle 
            title="Our Services" 
            subtitle="Professional drone services tailored to your specific needs"
            className="mb-0"
          />
        </Container>
      </section>

      {/* Intro Section */}
      <section className="py-12 bg-brand-black">
        <Container>
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-lg text-white mb-6">
              Professional aerial photography, videography, and mapping—bringing new perspective to projects across Texas..  
            </p>
            <div className="flex justify-center">
              <Button href="/contact">Get a Free Quote</Button>
            </div>
          </div>
        </Container>
      </section>

      {/* Active Service Sections */}
      {activeServices.map((service, index) => (
        <section 
          key={service.id} 
          id={service.id}
          className="py-16 md:py-24 bg-brand-black scroll-mt-32"
        >
          <Container>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Service Text - Always on the left */}
              <div>
                <div className="flex items-center mb-4">
                  <div className="bg-brand-blue-600/10 p-3 rounded-full mr-4">
                    <Icon name={service.icon} className="text-brand-blue-600" size={28} />
                  </div>
                  <h2 className="text-3xl font-bold text-white">{service.title}</h2>
                </div>
                <p className="text-lg text-white mb-6">{service.description}</p>
                <h3 className="font-bold text-xl text-white mb-3">Benefits</h3>
                <ul className="space-y-2 mb-6">
                  {service.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-start">
                      <svg className="w-5 h-5 text-brand-blue-600 mr-2 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-white">{benefit}</span>
                    </li>
                  ))}
                </ul>
                <h3 className="font-bold text-xl text-white mb-3">Deliverables</h3>
                <p className="text-white mb-6">{service.deliverables}</p>
                <Button href={`/contact?service=${service.id}`}>Request a Quote</Button>
              </div>
              
              {/* Service Image - Always on the right */}
              {service.hasCarousel ? (
                <ImageCarousel
                  images={service.carouselImages.map(img => img.replace(/\.svg$/, '.png'))}
                  title={service.title}
                />
              ) : service.hasVideo ? (
                <VideoThumbnail
                  thumbnailUrl={service.videoThumbnail}
                  videoEmbedUrl={service.videoEmbedUrl}
                  title={service.title}
                />
              ) : (
                <div className="rounded-2xl overflow-hidden shadow-card">
                  <OptimizedImage 
                    src={service.image} 
                    alt={service.title} 
                    className="mx-auto" /* Center the image */
                    style={{ 
                      maxWidth: '100%', /* Ensure image doesn't overflow container width */
                      height: 'auto', /* Maintain aspect ratio */
                      objectFit: 'contain' /* Maintain aspect ratio */
                    }}
                  />
                </div>
              )}
            </div>
          </Container>
        </section>
      ))}

      {/* No Coming Soon Services Section anymore */}

      {/* CTA Section */}
      <section className="py-16 bg-brand-black text-white">
        <Container>
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-4">Ready to Elevate Your Project?</h2>
            <p className="text-lg mb-8">
              Contact us today to discuss your specific needs and get a customized quote for your aerial project.
            </p>
            <Button href="/contact">Get a Free Quote</Button>
          </div>
        </Container>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
